import React, { useEffect, useLayoutEffect, useRef, useState } from "react";

// A desktop hover card: a small non-interactive card that floats next to
// its anchor (extracted from ForCoaches/v2/AthleteHoverCard, which now
// composes it).
//
//   HoverCard({ anchorRef, open, onClose, children, placement, arrow, id, as })
//
// - Render it INSIDE the anchor's positioned wrapper (a `relative` or
//   `absolute` element): the card is absolutely placed against it.
// - `placement` is the preferred side, "bottom" (default) or "top", with an
//   optional alignment: "-start" lines the card's left edge up with the
//   anchor, "-end" its right edge ("top-start", "bottom-end"); no suffix
//   centres it. It flips to the other side when there is no room and shifts
//   sideways to stay on screen, correcting for the shell's CSS zoom.
// - Esc closes it (onClose), from anywhere while it is open.
// - role="tooltip" and pointer-events-none: it never takes focus or clicks;
//   give it an `id` and point the anchor's aria-describedby at it.
// - `arrow` (default true) draws the 16px arrow on centred placements.
// - Phones have no hover: the charts open the same detail in a TapPanel.
//
// useHoverCard() is the anchor side: { open, show, hide, anchorRef,
// anchorProps } where anchorProps opens on mouse enter or focus and closes
// on mouse leave, blur or Esc. HoverCardPanel is the standard card chrome
// (12px corners, border, shadow) for a detail body.

const EDGE = 8; // px kept clear of the viewport edges

const cx = (...parts) => parts.filter(Boolean).join(" ");

// Whether the card goes above its anchor, and the sideways shift (in the
// card's own CSS px, which differ from screen px under the shell's zoom)
// that keeps it on screen. `prefer` is the side it would rather take.
export const hoverPlacementFor = (anchor, card, cssWidth, prefer = "bottom") => {
  const below = window.innerHeight - anchor.bottom;
  const above = anchor.top;
  const up =
    prefer === "top" ? !(above < card.height + EDGE && below > above) : below < card.height + EDGE && anchor.top > below;
  const overflow =
    card.left < EDGE ? EDGE - card.left : card.right > window.innerWidth - EDGE ? window.innerWidth - EDGE - card.right : 0;
  const zoom = cssWidth ? card.width / cssWidth : 1;
  return { up, shift: overflow / (zoom || 1) };
};

const parsePlacement = (placement = "bottom") => {
  const [side, align] = String(placement).split("-");
  return { side: side === "top" ? "top" : "bottom", align: align === "start" || align === "end" ? align : "center" };
};

const Arrow = ({ up }) => (
  <svg width="16" height="8" viewBox="0 0 16 8" aria-hidden="true" className={up ? "-mb-px" : "-mt-px rotate-180"}>
    <path d="M0 8 L8 0 L16 8" fill="#fff" stroke="#E8E8EC" />
  </svg>
);

const ALIGN = {
  center: { box: "left-1/2 items-center", base: "-50%" },
  start: { box: "left-0 items-start", base: "0px" },
  end: { box: "right-0 items-end", base: "0px" },
};

// Mounted fresh on every open, so each open measures from an unshifted card.
const FloatingCard = ({ anchorRef, onClose, children, placement, arrow, id, as: Tag }) => {
  const { side, align } = parsePlacement(placement);
  const [place, setPlace] = useState({ up: side === "top", shift: 0 });
  const cardRef = useRef(null);
  const onCloseRef = useRef(onClose);

  useLayoutEffect(() => {
    onCloseRef.current = onClose;
  });

  useLayoutEffect(() => {
    const anchor = anchorRef?.current;
    const card = cardRef.current;
    if (!anchor || !card) return;
    setPlace(hoverPlacementFor(anchor.getBoundingClientRect(), card.getBoundingClientRect(), card.offsetWidth, side));
  }, [anchorRef, side]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onCloseRef.current?.();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  const a = ALIGN[align];
  const withArrow = arrow && align === "center";
  return (
    <Tag
      ref={cardRef}
      id={id}
      role="tooltip"
      className={cx(
        "pointer-events-none absolute z-40 flex",
        a.box,
        place.up ? "bottom-full flex-col-reverse pb-1" : "top-full flex-col pt-1",
      )}
      style={{ transform: `translateX(calc(${a.base} + ${place.shift}px))` }}
    >
      {withArrow && (
        // Above the card, so its white fill covers the card border where they meet.
        <span style={{ transform: `translateX(${-place.shift}px)` }} className="relative z-[1] flex">
          <Arrow up={!place.up} />
        </span>
      )}
      {children}
    </Tag>
  );
};

export const HoverCard = ({ anchorRef, open, onClose, children, placement = "bottom", arrow = true, id, as = "span" }) =>
  open ? (
    <FloatingCard anchorRef={anchorRef} onClose={onClose} placement={placement} arrow={arrow} id={id} as={as}>
      {children}
    </FloatingCard>
  ) : null;

// The anchor side of a hover card: hover or keyboard focus opens it, mouse
// leave, blur or Esc closes it.
export const useHoverCard = () => {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef(null);
  const show = () => setOpen(true);
  const hide = () => setOpen(false);
  const anchorProps = {
    onMouseEnter: show,
    onMouseLeave: hide,
    onFocus: show,
    onBlur: hide,
    onKeyDown: (e) => {
      if (e.key === "Escape") hide();
    },
  };
  return { open, show, hide, anchorRef, anchorProps };
};

// The standard hover card body: white, 12px corners, the bm border and the
// card shadow, 260px wide (under every phone width, though phones use a
// TapPanel instead).
export const HoverCardPanel = ({ children, className = "" }) => (
  <span
    className={cx(
      "flex w-[260px] flex-col gap-2 overflow-hidden rounded-[12px] border border-bm-border bg-white p-3 text-left shadow-[0_8px_24px_rgba(0,0,0,0.14)]",
      className,
    )}
  >
    {children}
  </span>
);

export default HoverCard;
